import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import "./Streak.css";
import {
  getStreak,
  applyReferral,
  dailyCheckIn,
} from "../services/streakApi";
import Navbar from "./Navbar";

// `compact` (Profile page only): a short card with the streak count, today's
// check-in status and the hint. Its action buttons (Daily Check-In, apply a
// referral code, copy the referral link) are rendered into `optionsTarget` —
// the Profile page's logo menu — so they stay hidden until that menu is open.
// Same component, same state, same single API call; the /streak page (no
// props) renders exactly as before.
function Streak({ compact = false, optionsTarget = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const [applyCode, setApplyCode] = useState("");
  const [applyMsg, setApplyMsg] = useState("");
  const [applyOk, setApplyOk] = useState(false);
  const [applying, setApplying] = useState(false);

  
  const getTodayIST = () => {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD
  }

  const isCheckedInToday = () => {
    if(!data?.lastCheckInDate) return false;
    const today = getTodayIST();
    const last = new Date(data.lastCheckInDate).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    return today === last;
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getStreak();
      setData(res);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Could not load your streak."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCheckIn = async () => {
    if(isCheckedInToday()) {
      alert("You already checked in today 🔥");
      return;
    }
    try {
      const res = await dailyCheckIn();
      setData((prev) => ({
      ...prev,
        streakCount: res.streakCount,
        lastCheckInDate: res.lastCheckInDate,
      }));
      alert(res.message);
    } catch (err) {
      alert(
        err?.response?.data?.message || "Could not check in."
      );
    }
  };

  const handleApplyReferral = async (e) => {
    e.preventDefault();
    if (!applyCode.trim()) return;

    setApplying(true);
    setApplyMsg("");
    setApplyOk(false);

    try {
      await applyReferral(applyCode.trim());
      setApplyOk(true);
      setApplyMsg("Referral applied ✅ +50 Points");
      setApplyCode("");
      load();
    } catch (err) {
      setApplyOk(false);
      setApplyMsg(
        err?.response?.data?.message || "Could not apply referral code."
      );
    } finally {
      setApplying(false);
    }
  };

  const handleCopy = async () => {
    if (!data?.referralLink) return;
    try {
      await navigator.clipboard.writeText(data.referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  if (compact) {
    const count = data?.streakCount || 0;
    const checked = isCheckedInToday();

    const options = loading ? (
      <p className="streak-menu-note">Loading streak…</p>
    ) : error ? (
      <p className="streak-menu-note streak-menu-error">{error}</p>
    ) : (
      <div className="streak-menu-options">
        <button
          type="button"
          className="streak-menu-checkin"
          onClick={handleCheckIn}
          disabled={checked}
        >
          {checked ? "✅ Already Checked In Today" : "✅ Daily Check-In"}
        </button>

        <form className="streak-menu-block" onSubmit={handleApplyReferral}>
          <span className="streak-menu-label">🎁 Have a Referral Code?</span>
          <div className="streak-menu-row">
            <input
              type="text"
              placeholder="Enter referral code"
              aria-label="Referral code"
              value={applyCode}
              onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
            />
            <button type="submit" disabled={applying || !applyCode.trim()}>
              {applying ? "Applying..." : "Apply"}
            </button>
          </div>
          {applyMsg && (
            <p className={`streak-menu-msg ${applyOk ? "active" : "inactive"}`}>{applyMsg}</p>
          )}
        </form>

        {data?.referralLink && (
          <div className="streak-menu-block">
            <span className="streak-menu-label">🔗 Your Referral Link</span>
            <div className="streak-menu-row">
              <input type="text" value={data.referralLink} readOnly aria-label="Your referral link" />
              <button type="button" onClick={handleCopy}>
                {copied ? "Copied ✅" : "📋 Copy"}
              </button>
            </div>
          </div>
        )}
      </div>
    );

    return (
      <>
        <div className="streak-compact" aria-busy={loading}>
          <div className="streak-compact-row">
            <span className="streak-compact-fire" aria-hidden="true">🔥</span>
            <div className="streak-compact-info">
              <span className="streak-compact-title">Daily Streak</span>
              {loading ? (
                <span className="streak-compact-sub">Loading…</span>
              ) : error ? (
                <span className="streak-compact-sub streak-compact-error">{error}</span>
              ) : (
                <span className="streak-compact-count">
                  {count} Day{count !== 1 && "s"}
                </span>
              )}
            </div>
            {!loading && !error && (
              <span className={`streak-compact-status ${checked ? "is-done" : "is-todo"}`}>
                {checked ? "✅ Checked in" : "⏳ Not checked in"}
              </span>
            )}
          </div>
          {!loading && !error && (
            <p className="streak-compact-hint">
              {checked
                ? "Come back tomorrow to continue your streak"
                : "Check in from the logo menu every day to continue your streak and earn rewards"}
            </p>
          )}
        </div>
        {optionsTarget && createPortal(options, optionsTarget)}
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Navbar/>
        <div className="streak-page">
          <div className="streak-card">
            <h1>🔥 Streak</h1>
            <p>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar/>
        <div className="streak-page">
          <div className="streak-card">
            <h1>🔥 Streak</h1>
            <p>{error}</p>
          </div>
        </div>
      </>
    );
  }

  const streakCount = data?.streakCount || 0;
  const alreadyChecked = isCheckedInToday();

  return (
    <>
      <Navbar/>
      <div className="streak-page">
        <div className="streak-card">
          <h1>🔥 Daily Streak</h1>

          <div className="streak-count-box">
            <span className="fire">🔥</span>
            <h2>{streakCount} Day{streakCount!== 1 && 's'}</h2>
          </div>

          <button
            className={`streak-btn ${alreadyChecked? 'disabled' : ''}`}
            onClick={handleCheckIn}
            disabled={alreadyChecked}
          >
            {alreadyChecked? "✅ Already Checked In Today" : "✅ Daily Check-In"}
          </button>

          <p className="streak-hint">
            {alreadyChecked
             ? "Come back tomorrow to continue your streak"
              : "Tap the button every day to continue your streak and earn rewards"}
          </p>

          <form className="streak-apply" onSubmit={handleApplyReferral}>
            <h3>🎁 Have a Referral Code?</h3>
            <div className="apply-row">
              <input
                type="text"
                placeholder="Enter referral code"
                value={applyCode}
                onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
              />
              <button type="submit" disabled={applying ||!applyCode.trim()}>
                {applying? "Applying..." : "Apply"}
              </button>
            </div>
            {applyMsg && (
              <p className={applyOk? "active" : "inactive"}>{applyMsg}</p>
            )}
          </form>

          {data?.referralLink && (
            <div className="streak-referral">
              <h3>🔗 Your Referral Link</h3>
              <p>Share with your friends and increase your streak! 🔥</p>
              <div className="copy-row">
                <input type="text" value={data.referralLink} readOnly />
                <button onClick={handleCopy}>
                  {copied? "Copied ✅" : "📋 Copy"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Streak;